import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import Layers from "slotworks/graphics/layers/layers";
import * as TweenJS from "slotworks/utils/tween";

export default class PaginationComponent {

    protected contentLayer: Layers;
    protected uiLayer: Layers;

    protected uiSceneName: string;
    protected contentSceneNames: string[];

    protected mode: PaginationMode;

    protected page: number;
    protected totalPages: number;

    protected scrollOrigin: number;
    protected pageWidth: number;
    protected scrollTween: TweenJS.Tween;

    protected nextButton: FlexiButton;
    protected prevButton: FlexiButton;
    protected closeButton: FlexiButton;

    protected pageJumpListeners: Map<FlexiButton, () => any> = new Map<FlexiButton, () => any>();

    constructor(contentLayer: Layers, uiLayer: Layers, uiSceneName: string, mode: PaginationMode = PaginationMode.PAGIFY) {

        this.contentSceneNames = [];
        this.contentLayer = contentLayer;
        this.uiLayer = uiLayer;
        this.uiSceneName = uiSceneName;
        this.mode = mode;

        for (let i = 1; i < Infinity; i++) {
            const page = "page_" + i.toString();
            if (!this.contentLayer.hasScene(page)) {
                break;
            }
            this.contentSceneNames.push(page);
        }

        this.totalPages = this.contentSceneNames.length;

        this.hide();
    }

    public show = () => {
        this.onShow();
    }

    public hide = () => {
        this.onHide();
    }

    protected onShow() {
        this.page = 0;

        this.contentLayer.show();

        if (this.mode === PaginationMode.SCROLL) {
            this.setupScrollableContent();
        }

        this.uiLayer.setScene(this.uiSceneName).execute();

        this.update();
    }

    protected onHide() {
        this.contentLayer.defaultScene().execute();
    }

    protected reset() {
        if (this.nextButton) {
            this.nextButton.off(ButtonEvent.POINTER_DOWN, this.nextPage);
        }
        if (this.prevButton) {
            this.prevButton.off(ButtonEvent.POINTER_DOWN, this.prevPage);
        }
        if (this.closeButton) {
            this.closeButton.off(ButtonEvent.POINTER_DOWN, this.hide);
        }

        this.pageJumpListeners.forEach((listener, button) => button.off(ButtonEvent.POINTER_DOWN, listener));
        this.pageJumpListeners.clear();
    }

    protected update() {
        if (this.mode === PaginationMode.PAGIFY) {
            this.contentLayer.setScene(this.contentSceneNames[this.page]).execute();
        } else {
            const target = this.scrollOrigin + this.pageWidth * (this.totalPages - this.page - 1);

            if (this.scrollTween) {
                this.scrollTween.stop();
            }

            this.scrollTween = new TweenJS.Tween(this.contentLayer.container)
                .to({ x: target }, Math.abs((target - this.contentLayer.container.x) / this.pageWidth) * 250)
                .start();
        }

        this.updateControls();

        const pageNumber = this.uiLayer.getText("page_number");
        if (pageNumber) {
            pageNumber.text = (this.page + 1) + "/" + (this.totalPages);
        }
    }

    protected updateControls() {
        this.reset();

        this.nextButton = this.uiLayer.getFlexiButton("next");
        this.prevButton = this.uiLayer.getFlexiButton("previous");
        this.closeButton = this.uiLayer.getFlexiButton("close");

        if (this.nextButton) {
            this.nextButton.on(ButtonEvent.POINTER_DOWN, this.nextPage);

            this.nextButton.setEnabled(this.page < this.totalPages - 1);
        }

        if (this.prevButton) {
            this.prevButton.on(ButtonEvent.POINTER_DOWN, this.prevPage);

            this.prevButton.setEnabled(this.page > 0);
        }

        if (this.closeButton) {
            this.closeButton.on(ButtonEvent.POINTER_DOWN, this.hide);
        }

        for (let i = 0; i < this.totalPages; i++) {
            const pageIndex = i;
            const pageName = this.contentSceneNames[pageIndex];
            const button = this.uiLayer.getFlexiButton(pageName);
            if (button) {
                button.setEnabled(true);

                const listener = () => this.jumpToPage(pageIndex);
                button.on(ButtonEvent.POINTER_DOWN, listener);
                this.pageJumpListeners.set(button, listener);

                if (pageIndex === this.page) {
                    button.setEnabled(false);
                }
            }
        }
    }

    protected jumpToPage(page: number) {
        this.page = page;

        this.update();
    }

    protected nextPage = () => {
        this.page++;
        this.update();
    }

    protected prevPage = () => {
        this.page--;
        this.update();
    }

    private setupScrollableContent() {
        const bounds = this.contentLayer.bounds;
        this.pageWidth = bounds.width;

        this.contentLayer.defaultScene().execute();

        for (const scene of this.contentSceneNames) {
            for (const child of this.contentLayer.container.children) {
                child.x -= this.pageWidth;
            }

            this.contentLayer.jumpToScene(scene, false);
        }

        this.scrollOrigin = this.contentLayer.container.x;
        this.contentLayer.container.x = this.scrollOrigin + this.pageWidth * (this.totalPages - 1);

        const mask = new PIXI.Graphics();
        mask.beginFill(0xff0000, 1);
        mask.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);

        this.contentLayer.container.mask = mask;
    }
}

export enum PaginationMode {
    PAGIFY = "pagify",
    SCROLL = "scroll"
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/ui/pagination-component.ts